import { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Formik, Form } from "formik";
import { TabView, TabPanel } from "primereact/tabview";
import { Select, MultiSelect } from "../../components";
import Layout from "../Layout/Layout";
import Spinner from "../../components/Spinner/Spinner";
import { Accordion, AccordionTab } from "primereact/accordion";
import { UserContext } from "../../components/Context/AuthUser";
import Checkboxes from "../../components/CheckBox/Checkboxes";
import Input from "../../components/Input/Input";
import Textarea from "../../components/Textarea/Textarea";
import InputsGroup from "../../components/InputsGroup/InputsGroup";
import Multicheckbox from "../../components/CheckBox/Multicheckbox";
import MatchedAdCard from "../../components/MatchedAdCard/MatchedAdCard";
import SendedEmailsHistory from "../../components/SendedEmailsHistory/SendedEmailsHistory";
import { AiOutlineLeft } from "react-icons/ai";
import { FiSave } from "react-icons/fi";
import { FaTrash } from "react-icons/fa";
import { BsGraphUpArrow } from "react-icons/bs";

import { RiMoneyEuroBoxLine } from "react-icons/ri";
import { BiBed } from "react-icons/bi";
import { GrRestroom } from "react-icons/gr";
import { BsCalendarX } from "react-icons/bs";
import { MdHeight } from "react-icons/md";
import { GiCancel, GiPapers } from "react-icons/gi";
import { getAllConsultants } from "../../api/consultants.api";
import {
  getAllResidentialZones,
  getAllPatrimonialZones,
  getAllCostaZones,
  getAllRusticAndSingularZones,
} from "../../api/zones.api";
import { getAllContacts } from "../../api/contacts.api";
import useWindowSize from "../../hooks/useWindowSize";
import {
  createRequest,
  getLastReference,
  getRequestById,
  updateRequest,
  deleteRequest,
} from "../../api/requests.api";
import {
  organizeCostaZones,
  organizeZonesByGroup,
  PATRIMONIAL_GROUPS,
  RESIDENTIAL_GROUPS,
  RUSTIC_SINGULAR_GROUPS,
} from "../../functions/ads/ad-functions";
import { checkIfIncludes, formatCurrency } from "../../utils/formatters";
import "./RequestForm.scss";
import { useRequestInitialValues } from "./useRequestInitialValues";
import {
  ALL_BUILDING_TYPES,
  DEPT_VALID_TYPES,
} from "../../constants/ads-requests-constants";
import { Tooltip } from "primereact/tooltip";
import reformado from "../../icons/qualities/Reformado.svg";
import aReformar from "../../icons/qualities/A reformar.svg";
import salidaDeHumos from "../../icons/qualities/Salida de humos.svg";
import coworking from "../../icons/qualities/Coworking.svg";
import edificioExclusivo from "../../icons/qualities/Edificio exclusivo de oficinas.svg";
import implantada from "../../icons/qualities/Implantada.svg";
import vistasAlMar from "../../icons/qualities/Vistas al mar.svg";
import campoDeGolf from "../../icons/qualities/Campo de golf.svg";
import seguridad24h from "../../icons/qualities/Seguridad 24h.svg";
import urbanizacionCerrada from "../../icons/qualities/Urbanizacion cerrada.svg";

const RequestForm = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { id } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const contactId = params.get("contactId");

  // Estados de Datos Maestro
  const [reference, setReference] = useState(0);
  const [requestById, setRequestById] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [residentials, setResidential] = useState([]);
  const [patrimonials, setPatrimonial] = useState([]);
  const [costas, setCostas] = useState([]);
  const [rusticAndSingulars, setRusticAndSingulars] = useState([]);

  const [resSelected, setResSelected] = useState([]);
  const [patSelected, setPatSelected] = useState([]);
  const [cosSelected, setCosSelected] = useState([]);
  const [rusSelected, setRusSelected] = useState([]);
  const [loadedZones, setLoadedZones] = useState(false);

  // Estados de UI
  const [loader, setLoader] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showMatching, setShowMatching] = useState(false);
  const [disabledSaveButton, setDisabledSaveButton] = useState(true);

  // Inicialización de valores memorizados
  const initialValues = useRequestInitialValues({
    id,
    requestById,
    reference,
    user,
    contactId,
  });

  const organizedPatrimonials = useMemo(
    () => organizeZonesByGroup(patrimonials, PATRIMONIAL_GROUPS),
    [patrimonials],
  );
  const organizedResidentials = useMemo(
    () => organizeZonesByGroup(residentials, RESIDENTIAL_GROUPS),
    [residentials],
  );

  const organizedRusticAndSingulars = useMemo(
    () => organizeZonesByGroup(rusticAndSingulars, RUSTIC_SINGULAR_GROUPS),
    [rusticAndSingulars],
  );

  const organizedCosta = useMemo(() => organizeCostaZones(costas), [costas]);

  useEffect(() => {
    // Solo ejecutamos si tenemos los datos de la petición y los catálogos cargados
    if (id && requestById && !loader && !loadedZones) {
      const zonesInRequest = requestById.requestZone || [];

      // Filtramos estrictamente por el campo `zone` real de la BD
      const res = residentials
        .filter(
          (z) => zonesInRequest.includes(z._id) && z.zone === "Residencial",
        )
        .map((z) => z._id);

      const pat = patrimonials
        .filter(
          (z) => zonesInRequest.includes(z._id) && z.zone === "Patrimonial",
        )
        .map((z) => z._id);

      const cos = costas
        .filter((z) => zonesInRequest.includes(z._id) && z.zone === "Costa")
        .map((z) => z._id);

      // ✨ ACTUALIZADO: Ya no buscamos "Others", solo la nueva zona
      const rus = rusticAndSingulars
        .filter(
          (z) =>
            zonesInRequest.includes(z._id) &&
            // He dejado ambas opciones por si en BD lo llamasteis de una forma u otra
            (z.zone === "Campos Rústicos & Activos Singulares" ||
              z.zone === "Campos Rústicos"),
        )
        .map((z) => z._id);

      setResSelected(res);
      setPatSelected(pat);
      setCosSelected(cos);
      setRusSelected(rus);

      setLoadedZones(true); // Marcamos como cargado para no repetir el proceso
    }
  }, [
    id,
    requestById,
    loader,
    residentials,
    patrimonials,
    costas,
    rusticAndSingulars,
    loadedZones,
  ]);

  useEffect(() => {
    const getFetchs = async () => {
      setLoader(true);
      try {
        const [cons, conts, resZ, patZ, cosZ, rusAndSingZ] = await Promise.all([
          getAllConsultants(),
          getAllContacts(),
          getAllResidentialZones(),
          getAllPatrimonialZones(),
          getAllCostaZones(),
          getAllRusticAndSingularZones(),
        ]);
        setConsultants(cons);
        setContacts(conts);
        setResidential(resZ);
        setPatrimonial(patZ);
        setCostas(cosZ);
        setRusticAndSingulars(rusAndSingZ);

        if (id) {
          const res = await getRequestById(id);
          setRequestById(res);
          // ACTIVAR MATCHING AQUÍ
          setShowMatching(true);
        } else {
          const ref = await getLastReference();
          setReference(ref);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoader(false);
      }
    };
    getFetchs();
  }, [id]);

  useEffect(() => {
    if (!user) navigate("/");
  }, [user, navigate]);

  const handleFormSubmit = async ({
    data,
    id,
    createRequest,
    updateRequest,
    navigate,
    setRequestById,
    setDisabledSaveButton,
  }) => {
    setDisabledSaveButton(false);

    // 1. UNIFICACIÓN DE ZONAS (Desde estados locales)
    // Como sacamos las zonas de Formik para que Syncfusion funcione,
    // debemos agruparlas manualmente antes de enviar.
    const allSelectedZones = [
      ...resSelected,
      ...patSelected,
      ...cosSelected,
      ...rusSelected,
    ];

    // 2. VALIDACIÓN DE ZONAS
    if (allSelectedZones.length === 0) {
      alert("Debe seleccionar al menos una zona geográfica.");
      setDisabledSaveButton(true);
      return;
    }

    // Asignamos las zonas unificadas al objeto de datos
    data.requestZone = allSelectedZones;

    // 3. VALIDACIÓN LÓGICA DE COMPATIBILIDAD (Zonas seleccionadas vs Tipos)
    const selectedTypes = data.requestBuildingType || [];

    if (selectedTypes.length > 0) {
      // a) Determinar qué departamentos ha seleccionado el usuario basándonos en las zonas
      const activeDepts = [];
      if (resSelected.length > 0) activeDepts.push("Residencial");
      if (patSelected.length > 0) activeDepts.push("Patrimonio"); // Exacto a tu constante
      if (cosSelected.length > 0) activeDepts.push("Costa");
      if (rusSelected.length > 0)
        activeDepts.push("Campos Rústicos & Activos Singulares"); // Exacto a tu constante

      let compatibleDepartment = null;

      // b) Validar si TODOS los tipos seleccionados son válidos en AL MENOS UNO de los departamentos activos
      for (const dept of activeDepts) {
        const allowedTypesForDept = DEPT_VALID_TYPES[dept] || [];

        // Comprobamos si este departamento admite todos los tipos que ha marcado el usuario
        const deptAdmitsAllTypes = selectedTypes.every((type) =>
          allowedTypesForDept.includes(type),
        );

        if (deptAdmitsAllTypes) {
          compatibleDepartment = dept;
          break; // Encontramos un departamento válido, no hace falta seguir buscando
        }
      }

      // Si no se encontró ningún departamento compatible, lanzamos el error y detenemos el guardado
      if (!compatibleDepartment) {
        alert(
          "🚨 ERROR DE COMPATIBILIDAD\n\n" +
            "Los tipos de inmueble seleccionados no son válidos para las zonas geográficas seleccionadas.\n" +
            "Por favor, revise la leyenda en el icono (?) junto al título.",
        );
        setDisabledSaveButton(true);
        return;
      }

      // Asignamos el departamento compatible
      data.department = compatibleDepartment;
    }

    // 4. MAPEADO Y FORMATEO DE DATOS PARA EL BACKEND
    // Convertimos strings vacíos en valores por defecto numéricos
    const finalData = {
      ...data,
      requestSalePrice: {
        salePriceMax: data.salePriceMax === "" ? 999999999 : data.salePriceMax,
        salePriceMin: data.salePriceMin === "" ? 0 : data.salePriceMin,
      },
      requestRentPrice: {
        rentPriceMax: data.rentPriceMax === "" ? 999999999 : data.rentPriceMax,
        rentPriceMin: data.rentPriceMin === "" ? 0 : data.rentPriceMin,
      },
      requestBuildSurface: {
        buildSurfaceMax:
          data.buildSurfaceMax === "" ? 999999999 : data.buildSurfaceMax,
        buildSurfaceMin: data.buildSurfaceMin === "" ? 0 : data.buildSurfaceMin,
      },
      requestPlotSurface: {
        plotSurfaceMax:
          data.plotSurfaceMax === "" ? 999999999 : data.plotSurfaceMax,
        plotSurfaceMin: data.plotSurfaceMin === "" ? 0 : data.plotSurfaceMin,
      },
      requestBedrooms: {
        bedroomsMax: data.bedroomsMax === "" ? 999 : data.bedroomsMax,
        bedroomsMin: data.bedroomsMin === "" ? 0 : data.bedroomsMin,
      },
      requestBathrooms: {
        bathroomsMax: data.bathroomsMax === "" ? 999 : data.bathroomsMax,
        bathroomsMin: data.bathroomsMin === "" ? 0 : data.bathroomsMin,
      },
    };

    // 5. EJECUCIÓN DE LA PETICIÓN API
    try {
      if (!id) {
        // Caso: Nueva Petición
        const res = await createRequest(finalData);
        alert(`✅ Petición ${res.requestReference} creada correctamente.`);
        navigate("/peticiones");
      } else {
        // Caso: Edición de Petición existente
        const res = await updateRequest(finalData);

        // Actualizamos el estado para reflejar cambios en Historial/Matching
        setRequestById(res);
        alert("✅ Petición actualizada correctamente.");
        setDisabledSaveButton(true);
      }
    } catch (error) {
      console.error("Error al guardar la petición:", error);
      alert("❌ Error: No se ha podido guardar la petición en el servidor.");
      setDisabledSaveButton(true);
    }
  };

  return (
    <>
      <Layout
        subTitle="Peticiones"
        subUndertitle={
          <button className="GoBack" onClick={() => navigate(-1)}>
            <AiOutlineLeft
              fontSize={"0.8em"}
              style={{ marginRight: 5, color: "#5C5C5C" }}
            />
            Volver
          </button>
        }
        subBreadcrumbs={
          id
            ? `Petición ${requestById?.requestReference || ""}`
            : "Crear nueva petición"
        }
        footContent={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "2rem",
              justifyContent: "center",
            }}
          >
            <button
              className={
                disabledSaveButton ? "buttonNormalActive" : "buttonDisabled"
              }
              type="submit"
              form="RequestForm"
              disabled={!disabledSaveButton}
            >
              <FiSave size={15} /> Guardar
            </button>
            <button
              className="buttonNormal"
              onClick={() => navigate("/peticiones")}
            >
              <GiCancel size={15} color="black" />
              Cancelar
            </button>
            {id && (
              <button
                className="buttonDelete"
                onClick={() => {
                  if (
                    window.confirm(
                      "¿Estás seguro de que deseas borrar esta petición? Esta acción no se puede deshacer.",
                    )
                  ) {
                    deleteRequest(id).then(() => navigate("/peticiones"));
                  }
                }}
              >
                <FaTrash size={12} /> Borrar
              </button>
            )}
          </div>
        }
      >
        <TabView
          activeIndex={activeIndex}
          onTabChange={(e) => setActiveIndex(e.index)}
        >
          <TabPanel header="Detalles">
            {!loader ? (
              <Formik
                enableReinitialize={!!requestById}
                initialValues={initialValues}
                onSubmit={(data) =>
                  handleFormSubmit({
                    data,
                    id,
                    createRequest,
                    updateRequest,
                    navigate,
                    setRequestById,
                    setDisabledSaveButton,
                  })
                }
              >
                {({ values, setFieldValue }) => (
                  <Form id="RequestForm" className="RequestForm">
                    <div className="RequestForm__container">
                      <div className="RequestForm__container__col">
                        <Select
                          label="Contacto"
                          list={contacts}
                          fields={{
                            groupBy: "",
                            text: "fullName",
                            value: "_id",
                          }}
                          filter={(e) => {
                            const searchData = contacts.filter(
                              (c) =>
                                checkIfIncludes(c.fullName, e.text) ||
                                checkIfIncludes(c.email, e.text) ||
                                checkIfIncludes(c.company, e.text),
                            );
                            e.updateData(searchData);
                          }}
                          fn={(e) =>
                            setFieldValue("requestContact", e.target.value)
                          }
                          defaultValues={values.requestContact}
                        />
                        <Select
                          label="Consultor"
                          list={consultants}
                          fields={{
                            groupBy: "",
                            text: "fullName",
                            value: "_id",
                          }}
                          fn={(e) =>
                            setFieldValue("requestConsultant", e.target.value)
                          }
                          defaultValues={values.requestConsultant}
                        />

                        <Tooltip target=".custom-target-icon" />

                        <div className="RequestForm__container__col">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              marginBottom: "5px",
                            }}
                          >
                            <label
                              className="RequestForm__label"
                              style={{ fontWeight: "bold", marginBottom: 0 }}
                            >
                              Tipo de inmueble
                            </label>
                            <i
                              className="pi pi-question-circle custom-target-icon"
                              data-pr-tooltip={`Para zonas de Patrimonio: Oficina, Edificio, Local, Nave, Suelo.
                                                Para zonas de Residencial: Casa, Piso, Parcela, Ático.
                                                Para zonas de Costa: Casa, Piso, Parcela, Ático, Bajo.
                                                Para zonas de Campos Rústicos: Finca, Casa de Campo, Activo Singular.`}
                              data-pr-position="right"
                              data-pr-at="right+5 top"
                              data-pr-my="left center"
                              style={{
                                fontSize: "1.5rem",
                                cursor: "pointer",
                                color: "#5c5c5c",
                              }}
                            ></i>
                          </div>
                        </div>
                        <Multicheckbox
                          required={values.requestBuildingType.length === 0}
                          inputs={ALL_BUILDING_TYPES.map((item) => ({
                            value: item.name,
                            label: item.name,
                            checked: values.requestBuildingType.includes(
                              item.name,
                            ),
                            onChange: (ev) => {
                              const val = ev.target.value;
                              const nextValue =
                                values.requestBuildingType.includes(val)
                                  ? values.requestBuildingType.filter(
                                      (v) => v !== val,
                                    )
                                  : [...values.requestBuildingType, val];
                              setFieldValue("requestBuildingType", nextValue);
                            },
                          }))}
                        />
                      </div>

                      <div className="RequestForm__container__col">
                        <Checkboxes
                          label="Tipo de anuncio"
                          textA="Alquiler"
                          checkedA={values.requestAdType.includes("Alquiler")}
                          onChangeA={() => {
                            const next = values.requestAdType.includes(
                              "Alquiler",
                            )
                              ? values.requestAdType.filter(
                                  (v) => v !== "Alquiler",
                                )
                              : [...values.requestAdType, "Alquiler"];
                            setFieldValue("requestAdType", next);
                          }}
                          textB="Venta"
                          checkedB={values.requestAdType.includes("Venta")}
                          onChangeB={() => {
                            const next = values.requestAdType.includes("Venta")
                              ? values.requestAdType.filter(
                                  (v) => v !== "Venta",
                                )
                              : [...values.requestAdType, "Venta"];
                            setFieldValue("requestAdType", next);
                          }}
                          required={values.requestAdType.length === 0}
                        />
                        <Input
                          value={values.requestReference}
                          label="Id Petición"
                          name="requestReference"
                          readOnly
                        />
                        <Textarea
                          label="Comentarios"
                          name="requestComment"
                          value={values.requestComment}
                          onChange={(ev) =>
                            setFieldValue("requestComment", ev.target.value)
                          }
                        />
                      </div>
                    </div>

                    <Accordion multiple>
                      <AccordionTab header="Zonas">
                        <div className="RequestForm__container">
                          <div className="RequestForm__container__col">
                            <MultiSelect
                              label="Zonas de Residencial"
                              list={organizedResidentials}
                              fields={{
                                groupBy: "zone",
                                text: "name",
                                value: "_id",
                              }}
                              value={resSelected} // Estado local
                              onChange={(ev) => setResSelected(ev.value)} // Setter local
                            />
                            <div style={{ marginTop: "1rem" }}>
                              <MultiSelect
                                label="Zonas de Costa"
                                list={organizedCosta}
                                fields={{
                                  groupBy: "zone",
                                  text: "name",
                                  value: "_id",
                                }}
                                value={cosSelected}
                                onChange={(ev) => setCosSelected(ev.value)}
                              />
                            </div>
                          </div>
                          <div className="RequestForm__container__col">
                            <MultiSelect
                              label="Zonas de Patrimonial"
                              list={organizedPatrimonials}
                              fields={{
                                groupBy: "zone",
                                text: "name",
                                value: "_id",
                              }}
                              value={patSelected}
                              onChange={(ev) => setPatSelected(ev.value)}
                            />
                            <div style={{ marginTop: "1rem" }}>
                              <MultiSelect
                                label="Zonas de Campos Rústicos & Activos Singulares"
                                list={organizedRusticAndSingulars}
                                fields={{
                                  groupBy: "zone",
                                  text: "name",
                                  value: "_id",
                                }}
                                value={rusSelected}
                                onChange={(ev) => setRusSelected(ev.value)}
                              />
                            </div>
                          </div>
                        </div>
                      </AccordionTab>

                      <AccordionTab header="Detalles">
                        <div className="RequestDetails__container">
                          <div className="RequestDetails__container__row">
                            {values.requestAdType.includes("Venta") && (
                              <div className="RequestDetails__container__row--item">
                                <InputsGroup
                                  label={
                                    <span className="RequestDetails__container__row--item-center">
                                      <RiMoneyEuroBoxLine />
                                      <span>Precio de venta</span>
                                    </span>
                                  }
                                  inputs={[
                                    {
                                      name: "salePriceMax",
                                      label: "Máximo",
                                      type: "text",
                                      value:
                                        values.salePriceMax === ""
                                          ? ""
                                          : formatCurrency(values.salePriceMax),
                                      onChange: (ev) => {
                                        let val = parseFloat(
                                          ev.target.value.replaceAll(".", ""),
                                        );
                                        setFieldValue(
                                          "salePriceMax",
                                          isNaN(val) ? "" : val,
                                        );
                                      },
                                      span: (
                                        <span
                                          style={{
                                            position: "absolute",
                                            right: "1%",
                                            top: "52%",
                                          }}
                                        >
                                          €
                                        </span>
                                      ),
                                    },
                                    {
                                      name: "salePriceMin",
                                      label: "Mínimo",
                                      type: "text",
                                      value:
                                        values.salePriceMin === ""
                                          ? ""
                                          : formatCurrency(values.salePriceMin),
                                      onChange: (ev) => {
                                        let val = parseFloat(
                                          ev.target.value.replaceAll(".", ""),
                                        );
                                        setFieldValue(
                                          "salePriceMin",
                                          isNaN(val) ? "" : val,
                                        );
                                      },
                                      span: (
                                        <span
                                          style={{
                                            position: "absolute",
                                            right: "1%",
                                            top: "52%",
                                          }}
                                        >
                                          €
                                        </span>
                                      ),
                                    },
                                  ]}
                                />
                              </div>
                            )}
                            {values.requestAdType.includes("Alquiler") && (
                              <div className="RequestDetails__container__row--item">
                                <InputsGroup
                                  label={
                                    <span className="RequestDetails__container__row--item-center">
                                      <BsCalendarX />
                                      <span>Precio del alquiler</span>
                                    </span>
                                  }
                                  inputs={[
                                    {
                                      name: "rentPriceMax",
                                      label: "Máximo",
                                      type: "text",
                                      value:
                                        values.rentPriceMax === ""
                                          ? ""
                                          : formatCurrency(values.rentPriceMax),
                                      onChange: (ev) => {
                                        let val = parseFloat(
                                          ev.target.value.replaceAll(".", ""),
                                        );
                                        setFieldValue(
                                          "rentPriceMax",
                                          isNaN(val) ? "" : val,
                                        );
                                      },
                                      span: (
                                        <span
                                          style={{
                                            position: "absolute",
                                            right: "1%",
                                            top: "52%",
                                          }}
                                        >
                                          €/mes
                                        </span>
                                      ),
                                    },
                                    {
                                      name: "rentPriceMin",
                                      label: "Mínimo",
                                      type: "text",
                                      value:
                                        values.rentPriceMin === ""
                                          ? ""
                                          : formatCurrency(values.rentPriceMin),
                                      onChange: (ev) => {
                                        let val = parseFloat(
                                          ev.target.value.replaceAll(".", ""),
                                        );
                                        setFieldValue(
                                          "rentPriceMin",
                                          isNaN(val) ? "" : val,
                                        );
                                      },
                                      span: (
                                        <span
                                          style={{
                                            position: "absolute",
                                            right: "1%",
                                            top: "52%",
                                          }}
                                        >
                                          €/mes
                                        </span>
                                      ),
                                    },
                                  ]}
                                />
                              </div>
                            )}
                          </div>
                          <div className="RequestDetails__container__row">
                            <div className="RequestDetails__container__row--item">
                              <InputsGroup
                                label={
                                  <span className="RequestDetails__container__row--item-center">
                                    <GiPapers />
                                    <span>Superficie construida</span>
                                  </span>
                                }
                                inputs={[
                                  {
                                    name: "buildSurfaceMax",
                                    label: "Máximo",
                                    value:
                                      values.buildSurfaceMax === ""
                                        ? ""
                                        : formatCurrency(
                                            values.buildSurfaceMax,
                                          ),
                                    onChange: (ev) =>
                                      setFieldValue(
                                        "buildSurfaceMax",
                                        parseFloat(
                                          ev.target.value.replaceAll(".", ""),
                                        ) || "",
                                      ),
                                    span: (
                                      <span
                                        style={{
                                          position: "absolute",
                                          right: "1%",
                                          top: "52%",
                                        }}
                                      >
                                        m²
                                      </span>
                                    ),
                                  },
                                  {
                                    name: "buildSurfaceMin",
                                    label: "Mínimo",
                                    value:
                                      values.buildSurfaceMin === ""
                                        ? ""
                                        : formatCurrency(
                                            values.buildSurfaceMin,
                                          ),
                                    onChange: (ev) =>
                                      setFieldValue(
                                        "buildSurfaceMin",
                                        parseFloat(
                                          ev.target.value.replaceAll(".", ""),
                                        ) || "",
                                      ),
                                    span: (
                                      <span
                                        style={{
                                          position: "absolute",
                                          right: "1%",
                                          top: "52%",
                                        }}
                                      >
                                        m²
                                      </span>
                                    ),
                                  },
                                ]}
                              />
                            </div>
                            <div className="RequestDetails__container__row--item">
                              <InputsGroup
                                label={
                                  <span className="RequestDetails__container__row--item-center">
                                    <MdHeight />
                                    <span>Superficie parcela</span>
                                  </span>
                                }
                                inputs={[
                                  {
                                    name: "plotSurfaceMax",
                                    label: "Máximo",
                                    value:
                                      values.plotSurfaceMax === ""
                                        ? ""
                                        : formatCurrency(values.plotSurfaceMax),
                                    onChange: (ev) =>
                                      setFieldValue(
                                        "plotSurfaceMax",
                                        parseFloat(
                                          ev.target.value.replaceAll(".", ""),
                                        ) || "",
                                      ),
                                    span: (
                                      <span
                                        style={{
                                          position: "absolute",
                                          right: "1%",
                                          top: "52%",
                                        }}
                                      >
                                        m²
                                      </span>
                                    ),
                                  },
                                  {
                                    name: "plotSurfaceMin",
                                    label: "Mínimo",
                                    value:
                                      values.plotSurfaceMin === ""
                                        ? ""
                                        : formatCurrency(values.plotSurfaceMin),
                                    onChange: (ev) =>
                                      setFieldValue(
                                        "plotSurfaceMin",
                                        parseFloat(
                                          ev.target.value.replaceAll(".", ""),
                                        ) || "",
                                      ),
                                    span: (
                                      <span
                                        style={{
                                          position: "absolute",
                                          right: "1%",
                                          top: "52%",
                                        }}
                                      >
                                        m²
                                      </span>
                                    ),
                                  },
                                ]}
                              />
                            </div>
                          </div>
                          <div className="RequestDetails__container__row">
                            <div className="RequestDetails__container__row--item">
                              <InputsGroup
                                label={
                                  <span className="RequestDetails__container__row--item-center">
                                    <BiBed />
                                    <span>Dormitorios</span>
                                  </span>
                                }
                                inputs={[
                                  {
                                    name: "bedroomsMax",
                                    label: "Máximo",
                                    type: "number",
                                    value: values.bedroomsMax,
                                    onChange: (ev) =>
                                      setFieldValue(
                                        "bedroomsMax",
                                        ev.target.value,
                                      ),
                                  },
                                  {
                                    name: "bedroomsMin",
                                    label: "Mínimo",
                                    type: "number",
                                    value: values.bedroomsMin,
                                    onChange: (ev) =>
                                      setFieldValue(
                                        "bedroomsMin",
                                        ev.target.value,
                                      ),
                                  },
                                ]}
                              />
                            </div>
                            <div className="RequestDetails__container__row--item">
                              <InputsGroup
                                label={
                                  <span className="RequestDetails__container__row--item-center">
                                    <GrRestroom />
                                    <span>Baños</span>
                                  </span>
                                }
                                inputs={[
                                  {
                                    name: "bathroomsMax",
                                    label: "Máximo",
                                    type: "number",
                                    value: values.bathroomsMax,
                                    onChange: (ev) =>
                                      setFieldValue(
                                        "bathroomsMax",
                                        ev.target.value,
                                      ),
                                  },
                                  {
                                    name: "bathroomsMin",
                                    label: "Mínimo",
                                    type: "number",
                                    value: values.bathroomsMin,
                                    onChange: (ev) =>
                                      setFieldValue(
                                        "bathroomsMin",
                                        ev.target.value,
                                      ),
                                  },
                                ]}
                              />
                            </div>
                          </div>
                          <div style={{ marginTop: "1rem" }}>
                            <span className="RequestDetails__container__row--item-center">
                              <span
                                style={{
                                  fontWeight: "bold",
                                  fontSize: "14px",
                                }}
                              >
                                Características
                              </span>
                            </span>
                            <div
                              className="RequestDetails__container__row"
                              style={{
                                display: "grid",
                                gridTemplateColumns:
                                  "repeat(auto-fit, minmax(180px, 1fr))",
                                justifyContent: "start", // Alinea todo el grid a la izquierda
                                gap: "20px 40px", // 20px de separación vertical, 40px de separación horizontal
                                padding: "1.3rem",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                  fontSize: "10px",
                                  gap: "1rem",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "5px",
                                    alignItems: "center",
                                  }}
                                >
                                  <img
                                    src={reformado}
                                    alt=""
                                    style={{ width: "1.5em", height: "auto" }}
                                  />
                                  <label
                                    style={{ marginBottom: 0 }}
                                    htmlFor="reformed"
                                  >
                                    Reformado
                                  </label>
                                </div>
                                <input
                                  type="checkbox"
                                  style={{ marginTop: 0 }}
                                  name="reformed"
                                  checked={values.reformed}
                                  onChange={(ev) =>
                                    setFieldValue(
                                      ev.target.name,
                                      !values.reformed,
                                    )
                                  }
                                />
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                  fontSize: "10px",
                                  gap: "1rem",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "5px",
                                    alignItems: "center",
                                  }}
                                >
                                  <img
                                    src={aReformar}
                                    alt=""
                                    style={{ width: "1.5em", height: "auto" }}
                                  />
                                  <label
                                    style={{ marginBottom: 0 }}
                                    htmlFor="toReform"
                                  >
                                    A reformar
                                  </label>
                                </div>
                                <input
                                  type="checkbox"
                                  style={{ marginTop: 0 }}
                                  name="toReform"
                                  checked={values.toReform}
                                  onChange={(ev) =>
                                    setFieldValue(
                                      ev.target.name,
                                      !values.toReform,
                                    )
                                  }
                                />
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                  fontSize: "10px",
                                  gap: "1rem",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "5px",
                                    alignItems: "center",
                                  }}
                                >
                                  <img
                                    src={salidaDeHumos}
                                    alt=""
                                    style={{ width: "1.5em", height: "auto" }}
                                  />
                                  <label
                                    style={{ marginBottom: 0 }}
                                    htmlFor="smokeOutlet"
                                  >
                                    Salida de Humos
                                  </label>
                                </div>
                                <input
                                  type="checkbox"
                                  style={{ marginTop: 0 }}
                                  name="smokeOutlet"
                                  checked={values.smokeOutlet}
                                  onChange={(ev) =>
                                    setFieldValue(
                                      ev.target.name,
                                      !values.smokeOutlet,
                                    )
                                  }
                                />
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                  fontSize: "10px",
                                  gap: "1rem",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "5px",
                                    alignItems: "center",
                                  }}
                                >
                                  <BsGraphUpArrow size={15} />
                                  <label
                                    style={{ marginBottom: 0 }}
                                    htmlFor="profitability"
                                  >
                                    Rentabilidad
                                  </label>
                                </div>
                                <input
                                  type="checkbox"
                                  style={{ marginTop: 0 }}
                                  name="profitability"
                                  checked={values.profitability}
                                  onChange={(ev) =>
                                    setFieldValue(
                                      ev.target.name,
                                      !values.profitability,
                                    )
                                  }
                                />
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                  fontSize: "10px",
                                  gap: "1rem",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "5px",
                                    alignItems: "center",
                                  }}
                                >
                                  <img
                                    src={coworking}
                                    alt=""
                                    style={{ width: "1.5em", height: "auto" }}
                                  />
                                  <label
                                    style={{ marginBottom: 0 }}
                                    htmlFor="coworking"
                                  >
                                    Coworking
                                  </label>
                                </div>
                                <input
                                  type="checkbox"
                                  style={{ marginTop: 0 }}
                                  name="coworking"
                                  checked={values.coworking}
                                  onChange={(ev) =>
                                    setFieldValue(
                                      ev.target.name,
                                      !values.coworking,
                                    )
                                  }
                                />
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                  fontSize: "10px",
                                  gap: "1rem",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "5px",
                                    alignItems: "center",
                                  }}
                                >
                                  <img
                                    src={edificioExclusivo}
                                    alt=""
                                    style={{ width: "1.5em", height: "auto" }}
                                  />
                                  <label
                                    style={{ marginBottom: 0 }}
                                    htmlFor="exclusiveOfficeBuilding"
                                  >
                                    Edicio Eclusivo de Oficinas
                                  </label>
                                </div>
                                <input
                                  type="checkbox"
                                  style={{ marginTop: 0 }}
                                  name="exclusiveOfficeBuilding"
                                  checked={values.exclusiveOfficeBuilding}
                                  onChange={(ev) =>
                                    setFieldValue(
                                      ev.target.name,
                                      !values.exclusiveOfficeBuilding,
                                    )
                                  }
                                />
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                  fontSize: "10px",
                                  gap: "1rem",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "5px",
                                    alignItems: "center",
                                  }}
                                >
                                  <img
                                    src={implantada}
                                    alt=""
                                    style={{ width: "1.5em", height: "auto" }}
                                  />
                                  <label
                                    style={{ marginBottom: 0 }}
                                    htmlFor="implanted"
                                  >
                                    Implantada
                                  </label>
                                </div>
                                <input
                                  type="checkbox"
                                  style={{ marginTop: 0 }}
                                  name="implanted"
                                  checked={values.implanted}
                                  onChange={(ev) =>
                                    setFieldValue(
                                      ev.target.name,
                                      !values.implanted,
                                    )
                                  }
                                />
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                  fontSize: "10px",
                                  gap: "1rem",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "5px",
                                    alignItems: "center",
                                  }}
                                >
                                  <img
                                    src={vistasAlMar}
                                    alt=""
                                    style={{ width: "1.5em", height: "auto" }}
                                  />
                                  <label
                                    style={{ marginBottom: 0 }}
                                    htmlFor="seaViews"
                                  >
                                    Vistas Al Mar
                                  </label>
                                </div>
                                <input
                                  type="checkbox"
                                  style={{ marginTop: 0 }}
                                  name="seaViews"
                                  checked={values.seaViews}
                                  onChange={(ev) =>
                                    setFieldValue(
                                      ev.target.name,
                                      !values.seaViews,
                                    )
                                  }
                                />
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                  fontSize: "10px",
                                  gap: "1rem",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "5px",
                                    alignItems: "center",
                                  }}
                                >
                                  <img
                                    src={campoDeGolf}
                                    alt=""
                                    style={{ width: "1.5em", height: "auto" }}
                                  />
                                  <label
                                    style={{ marginBottom: 0 }}
                                    htmlFor="golfCourseView"
                                  >
                                    Campo de Golf
                                  </label>
                                </div>
                                <input
                                  type="checkbox"
                                  style={{ marginTop: 0 }}
                                  name="golfCourseView"
                                  checked={values.golfCourseView}
                                  onChange={(ev) =>
                                    setFieldValue(
                                      ev.target.name,
                                      !values.golfCourseView,
                                    )
                                  }
                                />
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                  fontSize: "10px",
                                  gap: "1rem",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "5px",
                                    alignItems: "center",
                                  }}
                                >
                                  <img
                                    src={seguridad24h}
                                    alt=""
                                    style={{ width: "1.5em", height: "auto" }}
                                  />
                                  <label
                                    style={{ marginBottom: 0 }}
                                    htmlFor="fullHoursSecurity"
                                  >
                                    Seguridad 24h
                                  </label>
                                </div>
                                <input
                                  type="checkbox"
                                  style={{ marginTop: 0 }}
                                  name="fullHoursSecurity"
                                  checked={values.fullHoursSecurity}
                                  onChange={(ev) =>
                                    setFieldValue(
                                      ev.target.name,
                                      !values.fullHoursSecurity,
                                    )
                                  }
                                />
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                  fontSize: "10px",
                                  gap: "1rem",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "5px",
                                    alignItems: "center",
                                  }}
                                >
                                  <img
                                    src={urbanizacionCerrada}
                                    alt=""
                                    style={{ width: "1.5em", height: "auto" }}
                                  />
                                  <label
                                    style={{ marginBottom: 0 }}
                                    htmlFor="gatedCommunity"
                                  >
                                    Urbanización Cerrada
                                  </label>
                                </div>
                                <input
                                  type="checkbox"
                                  style={{ marginTop: 0 }}
                                  name="gatedCommunity"
                                  checked={values.gatedCommunity}
                                  onChange={(ev) =>
                                    setFieldValue(
                                      ev.target.name,
                                      !values.gatedCommunity,
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </AccordionTab>
                    </Accordion>
                  </Form>
                )}
              </Formik>
            ) : (
              <Spinner />
            )}
          </TabPanel>

          {/* PESTAÑA HISTORIAL */}
          {id && (
            <TabPanel header="Historial">
              {!loader && requestById ? (
                <SendedEmailsHistory requestById={requestById} />
              ) : (
                <Spinner />
              )}
            </TabPanel>
          )}

          {/* PESTAÑA MATCHING */}
          {showMatching && id && (
            <TabPanel header="Matching">
              {!loader && requestById ? (
                <MatchedAdCard
                  patrimonials={patrimonials}
                  residentials={residentials}
                  costas={costas}
                  rusticAndSingulars={rusticAndSingulars}
                  requestById={requestById}
                />
              ) : (
                <Spinner />
              )}
            </TabPanel>
          )}
        </TabView>
      </Layout>
    </>
  );
};

export default RequestForm;
